import {useRef, useState, type ReactNode} from 'react';
import {useQuery} from '@tanstack/react-query';
import {TriangleAlert, X} from 'lucide-react';
import {Alert, AlertDescription} from '@kroll-resolver-crisp/ui-components-react';
import {cn} from '@/lib/utils';
import {SensitiveMediaButton} from '@/components/shared/SensitiveMedia';
import {ImageLightbox} from '@/components/shared/ImageLightbox';
import {useStoredImageUrl} from '@/components/shared/useStoredImageUrl';
import {classificationsApi, type ClassificationAttachment} from '@/api/classifications';

// Verticals where attaching a screenshot is discouraged: the material is too sensitive to be copied into
// another system. Warned about, not blocked - an analyst may still have a reason.
const SCREENSHOT_DISCOURAGED_VERTICALS = new Set(['Child Safety']);

export const discouragesScreenshots = (verticalNames: readonly string[]) =>
  verticalNames.some((name) => SCREENSHOT_DISCOURAGED_VERTICALS.has(name));

// Mirrors the server's cap (CreateClassificationRequest.MaxAttachments).
const MAX_SCREENSHOTS = 5;

/// Wraps a notes textarea (children): pasting an image into it, or picking one below it, attaches a
/// screenshot. Uploads happen immediately and only the returned hashes are kept; they ride the
/// classify request as `attachment_sha256s`. Internal QA evidence - never delivered to the client.
export function NoteScreenshots({
  shas,
  onChange,
  discouraged = false,
  children
}: {
  readonly shas: string[];
  readonly onChange: (shas: string[]) => void;
  // A restricted vertical is selected: show a red warning against attaching screenshots.
  readonly discouraged?: boolean;
  readonly children: ReactNode;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const upload = async (files: File[]) => {
    setError(null);
    setBusy(true);
    // Local, so several files in one paste/pick accumulate rather than each overwriting the last.
    let current = shas;
    try {
      for (const file of files.slice(0, MAX_SCREENSHOTS - current.length)) {
        const {sha256} = await classificationsApi.uploadAttachment(file);
        if (!current.includes(sha256)) {
          current = [...current, sha256];
          onChange(current);
        }
      }
    } catch {
      setError('Could not upload that image. Use a PNG, JPEG, GIF or WebP under 5 MB.');
    } finally {
      setBusy(false);
    }
  };

  return (
    // Only pastes inside this subtree (the notes textarea) are intercepted, never the whole window.
    <div
      className="space-y-1.5"
      onPaste={(event) => {
        const files = [...event.clipboardData.items]
          .filter((item) => item.type.startsWith('image/'))
          .map((item) => item.getAsFile())
          .filter((file): file is File => file !== null);
        if (files.length > 0) {
          event.preventDefault();
          void upload(files);
        }
      }}>
      {children}
      {discouraged ? (
        <Alert variant="destructive">
          <TriangleAlert />
          <AlertDescription>
            You have selected a restricted vertical (Child Safety). Screenshots aren&apos;t
            recommended.
          </AlertDescription>
        </Alert>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        {shas.map((sha) => (
          <div key={sha} className="relative">
            <StoredImage sha={sha} alt="Attached screenshot" className="size-14" />
            <button
              type="button"
              aria-label="Remove screenshot"
              className="bg-background border-border absolute -top-1.5 -right-1.5 rounded-full border p-0.5"
              onClick={() => {
                onChange(shas.filter((s) => s !== sha));
              }}>
              <X className="size-3" />
            </button>
          </div>
        ))}
        {shas.length < MAX_SCREENSHOTS ? (
          <button
            type="button"
            disabled={busy}
            className={cn(
              'text-xs underline',
              discouraged
                ? 'text-destructive-ink font-medium'
                : 'text-muted-foreground hover:text-foreground'
            )}
            onClick={() => inputRef.current?.click()}>
            {busy ? 'Uploading…' : 'Attach screenshot (or paste)'}
          </button>
        ) : null}
        <input
          ref={inputRef}
          multiple
          hidden
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          onChange={(event) => {
            void upload([...(event.target.files ?? [])]);
            event.target.value = '';
          }}
        />
      </div>
      {error ? <p className="text-destructive-ink text-xs">{error}</p> : null}
    </div>
  );
}

/// Read-only thumbnails of a classification's screenshots for QA views; renders nothing when there are none.
export function ClassificationScreenshots({
  classificationId
}: {
  readonly classificationId: string | null;
}) {
  const {data} = useQuery({
    queryKey: ['classification-attachments', classificationId],
    queryFn: async () => classificationsApi.attachments(classificationId!),
    enabled: classificationId !== null
  });
  const attachments: ClassificationAttachment[] = data?.attachments ?? [];
  if (attachments.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {attachments.map((a) => (
        <StoredImage key={a.sha256} sha={a.sha256} alt="Analyst screenshot" className="size-28" />
      ))}
    </div>
  );
}

// Our API needs the bearer token, which a plain <img src> never sends - hence the blob fetch. A
// screenshot is as sensitive as the platform media it captures, so it is blurred like any thumbnail,
// and click opens the full-size lightbox at the blur state the analyst left it in.
function StoredImage({
  sha,
  alt,
  className
}: {
  readonly sha: string;
  readonly alt: string;
  // Size only; the frame (border, corners) is applied here.
  readonly className: string;
}) {
  const url = useStoredImageUrl(sha, classificationsApi.attachmentImage);
  const [open, setOpen] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [colourised, setColourised] = useState(false);
  const frame = cn('border-border shrink-0 overflow-hidden rounded-md border', className);
  if (!url) {
    return <div className={cn(frame, 'bg-muted')} />;
  }

  return (
    <>
      <SensitiveMediaButton
        compact
        label={`Enlarge: ${alt}`}
        src={url}
        alt={alt}
        className={cn(frame, 'cursor-zoom-in')}
        imageClassName="size-full object-cover"
        onClick={(blurred, greyed) => {
          setRevealed(!blurred);
          setColourised(!greyed);
          setOpen(true);
        }}
      />
      <ImageLightbox
        open={open}
        imageUrl={url}
        alt={alt}
        revealed={revealed}
        colourised={colourised}
        onClose={() => {
          setOpen(false);
        }}
      />
    </>
  );
}
